import React, { useState } from 'react';
import { cn } from '../../utils';
import { Button } from '../ui/button';
import { Input, Select } from '../ui/form';
import { useDivisionRoles } from '../../hooks/use-division-roles';
import type { CreateWorkflowStepDto } from '../../types';

/**
 * Represents a draft workflow identifier before it's assigned to a MatchIdentifier entity
 */
export interface DraftIdentifier {
  id: string; // Local unique ID for key purposes
  name: string;
  jsonPath: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex' | 'greaterThan' | 'lessThan';
  value?: string;
  regexPattern?: string;
  isRequired: boolean;
  weight: number;
}

/**
 * Represents a draft routing rule
 */
export interface DraftRoutingRule {
  id: string; // Local unique ID for key purposes
  field: string;
  operator: string;
  value: string;
}

/**
 * Represents the complete draft workflow state
 */
export interface DraftWorkflowState {
  name: string;
  description: string;
  identifiers: DraftIdentifier[];
  routingRules: DraftRoutingRule[];
  workflowSteps: CreateWorkflowStepDto[];
  isExpanded: boolean;
}

interface DraftWorkflowBuilderProps {
  draft: DraftWorkflowState;
  onChange: (draft: DraftWorkflowState) => void;
  onCreateWorkflow: () => void;
  onClear: () => void;
  isCreating?: boolean;
  divisionId: string;
  className?: string;
}

export function DraftWorkflowBuilder({
  draft,
  onChange,
  onCreateWorkflow,
  onClear,
  isCreating = false,
  divisionId,
  className,
}: DraftWorkflowBuilderProps) {
  const [activeSection, setActiveSection] = useState<'routingRules' | 'approval' | 'basic'>('routingRules');

  const hasRoutingRules = draft.routingRules.length > 0;
  const hasRequiredFields = draft.name.trim().length > 0 && hasRoutingRules;

  const handleRemoveIdentifier = (id: string) => {
    onChange({
      ...draft,
      identifiers: draft.identifiers.filter(i => i.id !== id),
    });
  };

  const handleRemoveRoutingRule = (id: string) => {
    onChange({
      ...draft,
      routingRules: draft.routingRules.filter(r => r.id !== id),
    });
  };

  const handleToggleRequired = (id: string) => {
    onChange({
      ...draft,
      identifiers: draft.identifiers.map(i =>
        i.id === id ? { ...i, isRequired: !i.isRequired } : i
      ),
    });
  };

  const handleUpdateWeight = (id: string, weight: number) => {
    onChange({
      ...draft,
      identifiers: draft.identifiers.map(i =>
        i.id === id ? { ...i, weight } : i
      ),
    });
  };

  // Simple step management
  const handleAddApprovalLevel = () => {
    const newLevel: CreateWorkflowStepDto = {
      step: draft.workflowSteps.length + 1,
      name: `Level ${draft.workflowSteps.length + 1}`,
      description: '',
      stepType: 'approval',
      approvers: {
        type: 'match_identifier_based',
        matchIdentifiers: [],
        parallelApproval: false,
        requiredApprovals: 1,
      },
      conditions: {
        amountThreshold: 0,
        skipIfApprovedBy: [],
      },
      canEditQuantity: false,
      orderLevelOnly: false,
    };
    onChange({
      ...draft,
      workflowSteps: [...draft.workflowSteps, newLevel],
    });
  };

  const handleRemoveApprovalLevel = (index: number) => {
    const newLevels = draft.workflowSteps.filter((_, i) => i !== index);
    // Re-number levels
    const renumbered = newLevels.map((level, idx) => ({
      ...level,
      step: idx + 1,
    }));
    onChange({
      ...draft,
      workflowSteps: renumbered,
    });
  };

  if (!draft.isExpanded) {
    // Collapsed state - show summary bar
    return (
      <div
        className={cn(
          'bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg cursor-pointer transition-all hover:from-blue-700 hover:to-blue-800',
          className
        )}
        onClick={() => onChange({ ...draft, isExpanded: true })}
      >
        <div className="px-4 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-hammer text-lg" />
            <span className="font-medium">Draft Workflow Builder</span>
            {draft.workflowSteps.length > 0 && (
              <span className="bg-white dark:bg-secondary-800/20 px-2 py-0.5 rounded text-sm">
                {draft.workflowSteps.length} level{draft.workflowSteps.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <i className="fa-solid fa-chevron-down" />
        </div>
      </div>
    );
  }

  // Expanded state - full builder
  return (
    <div className={cn('bg-white dark:bg-secondary-800 border-2 border-blue-500 rounded-lg shadow-lg', className)}>
      {/* Header */}
      <div
        className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center justify-between text-white cursor-pointer rounded-t-lg"
        onClick={() => onChange({ ...draft, isExpanded: false })}
      >
        <div className="flex items-center gap-3">
          <i className="fa-solid fa-hammer text-lg" />
          <span className="font-medium">Draft Workflow Builder</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="text-sm text-white/80 hover:text-white transition-colors"
          >
            <i className="fa-solid fa-trash mr-1" />
            Clear
          </button>
          <i className="fa-solid fa-chevron-up" />
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex border-b border-gray-200 dark:border-secondary-700">
        <button
          onClick={() => setActiveSection('routingRules')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium transition-colors',
            activeSection === 'routingRules'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-950'
              : 'text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200'
          )}
        >
          <i className="fa-solid fa-route mr-2" />
          Routing Rules
          {draft.routingRules.length > 0 && (
            <span className="ml-2 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-400 px-1.5 py-0.5 rounded-full text-xs">
              {draft.routingRules.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSection('approval')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium transition-colors',
            activeSection === 'approval'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-950'
              : 'text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200'
          )}
        >
          <i className="fa-solid fa-list-check mr-2" />
          Steps
          {draft.workflowSteps.length > 0 && (
            <span className="ml-2 bg-blue-100 dark:bg-blue-900 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">
              {draft.workflowSteps.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSection('basic')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium transition-colors',
            activeSection === 'basic'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-950'
              : 'text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200'
          )}
        >
          <i className="fa-solid fa-info-circle mr-2" />
          Basic Info
          {draft.name && (
            <i className="fa-solid fa-check ml-2 text-green-500 text-xs" />
          )}
        </button>
      </div>

      {/* Section Content */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {activeSection === 'routingRules' && (
          <RoutingRulesSection
            routingRules={draft.routingRules}
            onRemove={handleRemoveRoutingRule}
          />
        )}

        {activeSection === 'approval' && (
          <ApprovalLevelsSection
            levels={draft.workflowSteps}
            onChange={(levels) => onChange({ ...draft, workflowSteps: levels })}
            onAdd={handleAddApprovalLevel}
            onRemove={handleRemoveApprovalLevel}
            divisionId={divisionId}
          />
        )}

        {activeSection === 'basic' && (
          <BasicInfoSection
            name={draft.name}
            description={draft.description}
            onNameChange={(name) => onChange({ ...draft, name })}
            onDescriptionChange={(description) => onChange({ ...draft, description })}
          />
        )}
      </div>

      {/* Footer with Create Button */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-secondary-400">
            {!hasRoutingRules && (
              <span className="text-orange-600 dark:text-orange-400">
                <i className="fa-solid fa-exclamation-triangle mr-1" />
                Add at least one routing rule
              </span>
            )}
            {hasRoutingRules && !draft.name && (
              <span className="text-orange-600 dark:text-orange-400">
                <i className="fa-solid fa-exclamation-triangle mr-1" />
                Enter a workflow name
              </span>
            )}
            {hasRequiredFields && (
              <span className="text-green-600 dark:text-green-400">
                <i className="fa-solid fa-check-circle mr-1" />
                Ready to create
              </span>
            )}
          </div>
          <Button
            onClick={onCreateWorkflow}
            disabled={!hasRequiredFields || isCreating}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isCreating ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="fa-solid fa-plus mr-2" />
                Create Workflow
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Sub-components

interface IdentifiersSectionProps {
  identifiers: DraftIdentifier[];
  onRemove: (id: string) => void;
  onToggleRequired: (id: string) => void;
  onUpdateWeight: (id: string, weight: number) => void;
}

function IdentifiersSection({ identifiers, onRemove, onToggleRequired, onUpdateWeight }: IdentifiersSectionProps) {
  if (identifiers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
        <i className="fa-solid fa-filter text-4xl mb-3 text-gray-300 dark:text-secondary-600" />
        <p className="font-medium">No identifiers added yet</p>
        <p className="text-sm mt-1">
          Use the "Use as Identifier" button on discovered fields to add matching criteria
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {identifiers.map((identifier) => (
        <div
          key={identifier.id}
          className="bg-gray-50 dark:bg-secondary-900 rounded-lg p-3 border border-gray-200 dark:border-secondary-700"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-white">{identifier.name}</span>
                <span className={cn(
                  'px-1.5 py-0.5 rounded text-xs',
                  identifier.isRequired
                    ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-400'
                    : 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400'
                )}>
                  {identifier.isRequired ? 'Required' : 'Optional'}
                </span>
              </div>
              <code className="text-xs text-gray-500 dark:text-secondary-400 block mt-1 truncate">
                {identifier.jsonPath}
              </code>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-600 dark:text-secondary-400">
                <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 px-1.5 py-0.5 rounded">
                  {identifier.operator}
                </span>
                {identifier.value && (
                  <span className="bg-gray-100 dark:bg-secondary-700 px-1.5 py-0.5 rounded truncate max-w-[150px]">
                    {identifier.value}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500 dark:text-secondary-400">Weight:</label>
                <input
                  type="number"
                  value={identifier.weight}
                  onChange={(e) => onUpdateWeight(identifier.id, parseInt(e.target.value) || 1)}
                  className="w-12 px-1 py-0.5 text-xs border border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  min={1}
                  max={100}
                />
              </div>
              <button
                onClick={() => onToggleRequired(identifier.id)}
                className="p-1 text-gray-400 dark:text-secondary-500 hover:text-blue-600 transition-colors"
                title={identifier.isRequired ? 'Make optional' : 'Make required'}
              >
                <i className={cn(
                  'fa-solid',
                  identifier.isRequired ? 'fa-star' : 'fa-star-half-stroke'
                )} />
              </button>
              <button
                onClick={() => onRemove(identifier.id)}
                className="p-1 text-gray-400 dark:text-secondary-500 hover:text-red-600 transition-colors"
                title="Remove"
              >
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface ApprovalLevelsSectionProps {
  levels: CreateWorkflowStepDto[];
  onChange: (levels: CreateWorkflowStepDto[]) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  divisionId: string;
}

function ApprovalLevelsSection({ levels, onChange, onAdd, onRemove, divisionId }: ApprovalLevelsSectionProps) {
  const { data: divisionRolesData } = useDivisionRoles(divisionId);
  const divisionRoles = divisionRolesData?.roles || [];

  const handleLevelChange = (index: number, updates: Partial<CreateWorkflowStepDto>) => {
    const newLevels = [...levels];
    newLevels[index] = { ...newLevels[index], ...updates };
    onChange(newLevels);
  };

  const handleDivisionRoleToggle = (levelIndex: number, roleId: string, isSelected: boolean) => {
    const level = levels[levelIndex];
    const currentRoleIds = level.approvers.divisionRoleIds || [];
    
    let newRoleIds: string[];
    if (isSelected) {
      newRoleIds = [...currentRoleIds, roleId];
    } else {
      newRoleIds = currentRoleIds.filter(id => id !== roleId);
    }
    
    handleLevelChange(levelIndex, {
      approvers: { 
        ...level.approvers, 
        divisionRoleIds: newRoleIds,
        // Clear deprecated field if it exists
        roles: undefined,
      },
    });
  };

  return (
    <div className="space-y-4">
      {levels.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
          <i className="fa-solid fa-list-check text-4xl mb-3 text-gray-300 dark:text-secondary-600" />
          <p className="font-medium">No steps defined</p>
          <p className="text-sm mt-1">Add steps to define who needs to approve orders</p>
        </div>
      ) : (
        <div className="space-y-3">
          {levels.map((level, index) => (
            <div
              key={index}
              className="bg-gray-50 dark:bg-secondary-900 rounded-lg p-3 border border-gray-200 dark:border-secondary-700"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900 dark:text-white">Level {level.step}</span>
                <button
                  onClick={() => onRemove(index)}
                  className="p-1 text-gray-400 dark:text-secondary-500 hover:text-red-600 transition-colors"
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Level name"
                  value={level.name}
                  onChange={(e) => handleLevelChange(index, { name: e.target.value })}
                  className="text-sm"
                />
                <Select
                  value={level.approvers.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    handleLevelChange(index, {
                      approvers: { 
                        ...level.approvers, 
                        type: newType,
                        // Initialize divisionRoleIds when switching to role_based
                        divisionRoleIds: newType === 'role_based' ? (level.approvers.divisionRoleIds || []) : undefined,
                        // Clear deprecated field
                        roles: undefined,
                      },
                    });
                  }}
                  className="text-sm"
                >
                  <option value="match_identifier_based">Match Identifier Based</option>
                  <option value="role_based">By Division Role</option>
                </Select>
              </div>
              
              {/* Division Role Selection - shown when type is role_based */}
              {level.approvers.type === 'role_based' && (
                <div className="mt-3 p-2 bg-white dark:bg-secondary-800 rounded border border-gray-200 dark:border-secondary-700">
                  <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-2">
                    Select Division Roles:
                  </label>
                  {divisionRoles.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-secondary-400 italic">
                      No division roles available. Create roles in the Division settings first.
                    </p>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {divisionRoles.map((role) => {
                        const isSelected = (level.approvers.divisionRoleIds || []).includes(role.id);
                        return (
                          <label
                            key={role.id}
                            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-secondary-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleDivisionRoleToggle(index, role.id, e.target.checked)}
                              className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                            <span className="text-sm text-gray-700 dark:text-secondary-200">{role.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {(level.approvers.divisionRoleIds || []).length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-secondary-400 mt-2">
                      {(level.approvers.divisionRoleIds || []).length} role(s) selected
                    </p>
                  )}
                </div>
              )}
              
              {level.conditions && (
                <div className="mt-2 flex items-center gap-2">
                  <label className="text-xs text-gray-500 dark:text-secondary-400">Amount Threshold:</label>
                  <input
                    type="number"
                    value={level.conditions.amountThreshold || 0}
                    onChange={(e) =>
                      handleLevelChange(index, {
                        conditions: { ...level.conditions, amountThreshold: parseFloat(e.target.value) || 0 },
                      })
                    }
                    className="w-24 px-2 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    min={0}
                    step={100}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onAdd}
        className="w-full py-2 border-2 border-dashed border-gray-300 dark:border-secondary-600 rounded-lg text-gray-500 dark:text-secondary-400 hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        <i className="fa-solid fa-plus mr-2" />
        Add Step
      </button>
    </div>
  );
}

interface RoutingRulesSectionProps {
  routingRules: DraftRoutingRule[];
  onRemove: (id: string) => void;
}

function RoutingRulesSection({ routingRules, onRemove }: RoutingRulesSectionProps) {
  if (routingRules.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-secondary-400">
        <i className="fa-solid fa-route text-4xl mb-2 text-purple-300" />
        <p>No routing rules added yet</p>
        <p className="text-sm mt-1">
          Use the "Use as Routing Rule" button on discovered fields to add routing rules
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {routingRules.map((rule) => (
        <div
          key={rule.id}
          className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-route text-purple-600 dark:text-purple-400" />
              <code className="text-xs bg-white dark:bg-secondary-800 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800">
                {rule.field}
              </code>
              <span className="text-xs text-purple-700 dark:text-purple-400 font-medium">
                {rule.operator}
              </span>
              <code className="text-xs bg-white dark:bg-secondary-800 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800">
                {rule.value}
              </code>
            </div>
          </div>
          <button
            onClick={() => onRemove(rule.id)}
            className="text-gray-400 dark:text-secondary-500 hover:text-red-600 transition-colors"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      ))}
    </div>
  );
}

interface BasicInfoSectionProps {
  name: string;
  description: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
}

function BasicInfoSection({ name, description, onNameChange, onDescriptionChange }: BasicInfoSectionProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
          Workflow Name <span className="text-red-500">*</span>
        </label>
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Enter a name for this workflow"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Describe when this workflow should be used"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
        />
      </div>
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <i className="fa-solid fa-info-circle text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Tip: Name your workflow descriptively</p>
            <p className="mt-1">
              Use names that describe when this workflow applies, like "High Value Orders (&gt;$10K)" 
              or "International Shipments - EMEA Region"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
